import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap, ZoomControl } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Play, Square, MapPin, Radio, Footprints, Locate, PawPrint } from "lucide-react";

// Fix default Leaflet marker icons in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const DEFAULT_CENTER = [28.5355, 77.3910]; // Noida (Sector 18)

function Recenter({ center }) {
  const map = useMap();
  useEffect(() => { if (center) map.setView(center, map.getZoom()); }, [center, map]);
  return null;
}

export default function Tracking() {
  const [pets, setPets] = useState([]);
  const [petId, setPetId] = useState("");
  const [walks, setWalks] = useState([]);
  const [live, setLive] = useState(null);
  const [userLoc, setUserLoc] = useState(null);

  // Centre the map on the user's GPS as soon as the page opens
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserLoc([pos.coords.latitude, pos.coords.longitude]),
      () => { /* fall back to default city */ },
      { enableHighAccuracy: false, timeout: 6000, maximumAge: 5 * 60 * 1000 },
    );
  }, []);
  const [mode, setMode] = useState("walks"); // walks | live
  const [walking, setWalking] = useState(false);
  const [currentWalk, setCurrentWalk] = useState([]);
  const walkIdRef = useRef(null);
  const watchIdRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/pets`).then(r => { setPets(r.data); setPetId(r.data[0]?.pet_id || ""); });
  }, []);

  useEffect(() => {
    if (!petId) return;
    axios.get(`${API}/tracking/walks/${petId}`).then(r => setWalks(r.data.walks));
    if (mode === "live") {
      const tick = () => axios.get(`${API}/tracking/live/${petId}`).then(r => setLive(r.data));
      tick();
      const id = setInterval(tick, 5000);
      return () => clearInterval(id);
    }
  }, [petId, mode]);

  const startWalk = async () => {
    if (!navigator.geolocation) { alert("Geolocation not supported in this browser."); return; }
    walkIdRef.current = null;
    setCurrentWalk([]);
    setWalking(true);
    watchIdRef.current = navigator.geolocation.watchPosition(async (pos) => {
      const point = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      setCurrentWalk(prev => [...prev, point]);
      const r = await axios.post(`${API}/tracking/ping`, { pet_id: petId, lat: point.lat, lng: point.lng, accuracy: pos.coords.accuracy, walk_id: walkIdRef.current });
      walkIdRef.current = r.data.walk_id;
    }, (err) => { console.warn(err); }, { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 });
  };

  const stopWalk = () => {
    if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
    watchIdRef.current = null;
    setWalking(false);
    axios.get(`${API}/tracking/walks/${petId}`).then(r => setWalks(r.data.walks));
  };

  const center = currentWalk.length > 0
    ? [currentWalk[currentWalk.length-1].lat, currentWalk[currentWalk.length-1].lng]
    : (live?.location ? [live.location.lat, live.location.lng] : (userLoc || DEFAULT_CENTER));

  return (
    <div className="space-y-4" data-testid="tracking-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold">Pet Tracking</h1>
          <p className="text-ink-soft text-sm">Record walks via your phone's GPS, or connect a smart collar for live tracking.</p>
        </div>
        <div className="flex gap-2 items-center">
          <select data-testid="tracking-pet" value={petId} onChange={e => setPetId(e.target.value)} className="bg-surface border border-line rounded-full px-4 py-2 font-bold text-sm">
            {pets.map(p => <option key={p.pet_id} value={p.pet_id}>{p.name}</option>)}
          </select>
          <div className="bg-surface border border-line rounded-full p-1 flex">
            <button data-testid="mode-walks" onClick={() => setMode("walks")} className={`px-3 py-1.5 rounded-full text-xs font-bold ${mode === "walks" ? "bg-ink text-white" : "text-ink-soft"}`}>Walks</button>
            <button data-testid="mode-live" onClick={() => setMode("live")} className={`px-3 py-1.5 rounded-full text-xs font-bold ${mode === "live" ? "bg-ink text-white" : "text-ink-soft"}`}>Live</button>
          </div>
        </div>
      </div>

      <div className="relative rounded-3xl overflow-hidden border border-line shadow-soft bg-surface-muted" style={{height: "min(62vh, 560px)"}} data-testid="tracking-map">
        <MapContainer center={center} zoom={14} style={{height: "100%", width: "100%"}} zoomControl={false}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OSM'/>
          <Recenter center={center}/>
          {mode === "walks" && walks.map(w => (
            <React.Fragment key={w.walk_id}>
              <Polyline positions={w.points.map(p => [p.lat, p.lng])} pathOptions={{color: "#FF6B35", weight: 4}}/>
              {w.points[0] && <Marker position={[w.points[0].lat, w.points[0].lng]}><Popup>Start · {new Date(w.started_at).toLocaleString()}</Popup></Marker>}
            </React.Fragment>
          ))}
          {currentWalk.length > 1 && <Polyline positions={currentWalk.map(p => [p.lat, p.lng])} pathOptions={{color: "#3DA5D9", weight: 5, dashArray: "8,8"}}/>}
          {mode === "live" && live?.location && (
            <Marker position={[live.location.lat, live.location.lng]}>
              <Popup>{live.mode === "mock_collar" ? "📡 Mock GPS collar — connect a device for real-time tracking." : "Last known location"}</Popup>
            </Marker>
          )}
          <ZoomControl position="topright"/>
        </MapContainer>

        {/* Floating map header — pet badge + mode pill */}
        <div className="absolute top-3 left-3 flex items-center gap-2 z-[400] pointer-events-none">
          <div className="bg-surface/95 backdrop-blur rounded-full pl-2 pr-3 py-1.5 shadow-soft border border-line inline-flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-brand/15 grid place-items-center"><PawPrint className="w-4 h-4 text-brand"/></div>
            <span className="text-xs font-extrabold leading-none">{pets.find(p => p.pet_id === petId)?.name || "Pet"}</span>
          </div>
          <div className={`backdrop-blur rounded-full px-3 py-1.5 shadow-soft border text-[10px] font-extrabold uppercase tracking-wider inline-flex items-center gap-1.5 ${mode === "live" ? "bg-sky/15 border-sky text-sky" : "bg-brand/15 border-brand text-brand"}`}>
            {mode === "live" ? <><span className="w-1.5 h-1.5 rounded-full bg-sky animate-pulse"/> Live</> : <>📍 Walks</>}
          </div>
        </div>

        {/* Bottom-left legend */}
        <div className="absolute bottom-3 left-3 z-[400] bg-surface/95 backdrop-blur rounded-2xl px-3 py-2 shadow-soft border border-line flex items-center gap-3 text-[11px] font-bold pointer-events-none">
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-1 rounded-full bg-brand"/> Past walks</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-1 rounded-full border-2 border-dashed border-sky"/> Live</span>
        </div>

        {/* Recenter to me */}
        <button
          data-testid="tracking-recenter"
          onClick={() => {
            if (!navigator.geolocation) return;
            navigator.geolocation.getCurrentPosition(
              (pos) => setUserLoc([pos.coords.latitude, pos.coords.longitude]),
              () => {},
              { enableHighAccuracy: true, timeout: 6000 }
            );
          }}
          className="absolute bottom-3 right-3 z-[400] bg-surface/95 backdrop-blur w-11 h-11 rounded-full shadow-hover border border-line grid place-items-center hover:bg-brand hover:text-white hover:border-brand transition-colors"
          title="Centre on my location"
        >
          <Locate className="w-4 h-4"/>
        </button>

        {/* Active-walk indicator */}
        {walking && (
          <div className="absolute top-3 right-3 z-[400] bg-danger text-white rounded-full pl-2 pr-3 py-1.5 shadow-hover inline-flex items-center gap-2 text-xs font-extrabold animate-pulse">
            <span className="w-2 h-2 rounded-full bg-white"/>
            REC · {currentWalk.length} pts
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="bg-surface border border-line rounded-3xl p-5 shadow-soft">
          <h3 className="font-heading text-lg font-bold mb-2 flex items-center gap-2"><Footprints className="w-4 h-4 text-brand"/> Walk recorder</h3>
          {!walking ? (
            <button data-testid="walk-start" onClick={startWalk} disabled={!petId} className="w-full bg-brand text-white rounded-full py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60 hover:bg-brand-hover">
              <Play className="w-4 h-4"/> Start walk
            </button>
          ) : (
            <button data-testid="walk-stop" onClick={stopWalk} className="w-full bg-danger text-white rounded-full py-3 font-bold inline-flex items-center justify-center gap-2 hover:opacity-90">
              <Square className="w-4 h-4"/> Stop walk · {currentWalk.length} pts
            </button>
          )}
          <p className="text-xs text-ink-soft mt-2">Uses your browser's GPS while the page is open.</p>
        </div>
        <div className="bg-surface border border-line rounded-3xl p-5 shadow-soft">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h3 className="font-heading text-lg font-bold flex items-center gap-2"><Radio className="w-4 h-4 text-sky"/> Live GPS (collar)</h3>
            {live?.collar?.enabled && <span className="bg-leaf/40 text-ink rounded-full px-2 py-0.5 text-[10px] font-bold inline-flex items-center gap-1">✓ Connected</span>}
          </div>
          {live?.collar?.enabled ? (
            <div>
              <p className="text-sm text-ink-soft">Receiving pings from your <strong>{live.collar.brand}</strong> collar (device {live.collar.device_id}).</p>
              <Link to={`/app/pets/${petId}/collar`} data-testid="tracking-manage-collar" className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-brand hover:underline">Manage collar →</Link>
            </div>
          ) : (
            <div>
              <p className="text-sm text-ink-soft">No collar connected. We're showing a mocked location for now.</p>
              <Link to={`/app/pets/${petId}/collar`} data-testid="tracking-connect-collar" className="mt-2 inline-flex bg-brand text-white rounded-full px-4 py-2 text-xs font-bold hover:bg-brand-hover items-center gap-1">
                <Radio className="w-3.5 h-3.5"/> Connect a GPS collar
              </Link>
            </div>
          )}
        </div>
      </div>

      <div className="bg-surface border border-line rounded-3xl p-5 shadow-soft">
        <h3 className="font-heading text-lg font-bold mb-2">Recent walks</h3>
        {walks.length === 0 && <div className="text-ink-soft text-sm">No walks yet.</div>}
        <div className="space-y-2">
          {walks.slice(-5).reverse().map(w => (
            <div key={w.walk_id} className="flex items-center justify-between text-sm bg-surface-muted rounded-2xl px-3 py-2">
              <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-brand"/> {new Date(w.started_at).toLocaleString()}</div>
              <div className="text-ink-soft text-xs">{w.points.length} pts</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
